import { isEmpty, keyBy, last, map, mapValues, orderBy, pickBy } from "lodash"

import Firebase, { NULL_ID } from "#classes/Firebase"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  DIGITAL_EVAL_TEMPLATE_IDS_BY_LEVEL,
  EVAL_LEVEL_ICONS,
  EVAL_LEVEL_LABELS_BY_LEVEL,
  OLD_CORE_EVAL_TEMPLATE_IDS_BY_LEVEL,
  SCULPTURE_EVAL_TEMPLATE_IDS_BY_LEVEL,
} from "#constants/evaluationConstants"
import {
  ALL_SKILL_LEVELS,
  CLASS_GROUP_TO_SKILL_TYPE,
  CoreSkillLevel,
  DigitalSkillLevel,
  SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import {
  EVAL_COLORS,
  EvaluationConstructor,
  EvaluationTrait,
  EvaluationTraitCategory,
  PrintEvalCurrentCheckboxes,
  PrintEvalData,
  PrintEvalNextCheckboxes,
} from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"

export const MAX_EVAL_SEMESTER = "2025 C"

export const getClampedEvalSemester = (semester: string) =>
  semester > MAX_EVAL_SEMESTER ? MAX_EVAL_SEMESTER : semester

export const generateEvalId = (
  studentId: Uuid,
  semester: string,
  classGroup: CLASS_GROUPS
) => {
  return `${studentId}-${semester}-${classGroup.toLowerCase()}`
}

export const getNextSkill = <
  T extends CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel,
>(
  currentSkill: T,
  classGroup: CLASS_GROUPS
): T => {
  const skillLevels = Object.keys(ALL_SKILL_LEVELS[classGroup])
  const nextSkillIndex =
    skillLevels.findIndex((skill) => skill === currentSkill) + 1
  const nextSkill =
    nextSkillIndex === skillLevels.length
      ? last(skillLevels)
      : skillLevels[nextSkillIndex]

  return nextSkill as T
}

/**
 * Determines if a skill progression represents a skill up promotion
 * A skill up occurs when the next skill has a higher index than the current skill,
 * or when the student has reached the highest level
 */
export const getIsSkillUp = (
  currentSkill: string | null | undefined,
  nextSkill: string | null | undefined,
  classGroup: CLASS_GROUPS
): boolean => {
  if (!currentSkill || !nextSkill || !classGroup) return false

  const skillLevels = Object.keys(ALL_SKILL_LEVELS[classGroup])
  const currentIndex = skillLevels.indexOf(currentSkill)
  const nextIndex = skillLevels.indexOf(nextSkill)

  // If either skill is not found in the array, return false
  if (currentIndex === -1 || nextIndex === -1) return false

  // Skill up if next skill has higher index, or current skill is the last skill
  return nextIndex > currentIndex || currentIndex === skillLevels.length - 1
}

/**
 * Gets the next color level (base skill without ^) for evaluation printing
 * This is used to show what the next color level requirements are, regardless of promotion
 */
export const getNextColorLevel = <
  T extends CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel,
>(
  currentSkill: T,
  classGroup: CLASS_GROUPS
): T => {
  // Remove the ^ suffix to get the base color level
  const baseSkill = currentSkill.replace("^", "")

  const skillLevels = Object.keys(ALL_SKILL_LEVELS[classGroup])
  const baseSkillIndex = skillLevels.findIndex((skill) => skill === baseSkill)

  // Find the next base skill level (without ^)
  // Skip the current base^ level to get to the next base level
  const nextBaseSkillIndex = baseSkillIndex + 2
  const nextBaseSkill =
    nextBaseSkillIndex >= skillLevels.length
      ? last(skillLevels)
      : skillLevels[nextBaseSkillIndex]

  return nextBaseSkill as T
}

export const getEvaluationStatusColor = (status?: string) => {
  if (!status) return "default"
  return EVAL_COLORS[status as keyof typeof EVAL_COLORS] || "default"
}

/**
 * Gets the evaluation template ID for a given skill level and class type
 * @param skillLevel - The skill level (e.g., 'BG', 'RO', 'ST')
 * @param isSkillUp - Whether the skill is going up (true) or down (false)
 * @param classType - The class type ("core", "digital", "sculpture")
 * @returns The template ID string
 */
export const getEvalTemplateId = (
  skillLevel: string,
  isSkillUp: boolean,
  classType: "core" | "digital" | "sculpture"
): string => {
  switch (classType) {
    case "core": {
      const levelData =
        OLD_CORE_EVAL_TEMPLATE_IDS_BY_LEVEL[
          skillLevel as keyof typeof OLD_CORE_EVAL_TEMPLATE_IDS_BY_LEVEL
        ]
      return levelData?.[isSkillUp ? "true" : "false"] || ""
    }
    case "digital": {
      const levelData =
        DIGITAL_EVAL_TEMPLATE_IDS_BY_LEVEL[
          skillLevel as keyof typeof DIGITAL_EVAL_TEMPLATE_IDS_BY_LEVEL
        ]
      return levelData?.[isSkillUp ? "true" : "false"] || ""
    }
    case "sculpture": {
      const levelData =
        SCULPTURE_EVAL_TEMPLATE_IDS_BY_LEVEL[
          skillLevel as keyof typeof SCULPTURE_EVAL_TEMPLATE_IDS_BY_LEVEL
        ]
      return levelData?.[isSkillUp ? "true" : "false"] || ""
    }
    default:
      return ""
  }
}

const TRAIT_KEYS_BY_TYPE = {
  [EvaluationTraitCategory.ART_SKILLS]: "art",
  [EvaluationTraitCategory.CREATIVITY]: "creativity",
  [EvaluationTraitCategory.SOCIAL_SKILLS]: "social",
}

// get checkbox data for evaluation printing
// Outputs { [templatekeytext]: traitName, [templatekeycounter]: checked }
// where template key is like currentarttext1, currentartcheck1, etc.
// Note: Missing fields will be filled with defaults on the server side
const getTraitDataForPrint = <
  T extends PrintEvalCurrentCheckboxes | PrintEvalNextCheckboxes,
>(
  prefix: "current" | "next",
  checkboxes: Record<string, boolean>,
  traitsById: Record<string, EvaluationTrait>
): T => {
  const result: T = {} as T

  // Group traits by category
  const traitsByCategory = {
    [EvaluationTraitCategory.ART_SKILLS]: [] as EvaluationTrait[],
    [EvaluationTraitCategory.CREATIVITY]: [] as EvaluationTrait[],
    [EvaluationTraitCategory.SOCIAL_SKILLS]: [] as EvaluationTrait[],
  }

  // Sort traits into categories
  map(traitsById, (trait) => {
    traitsByCategory[trait.category].push(trait)
  })

  // Generate fields for each category based on available traits
  for (const [category, traits] of Object.entries(traitsByCategory)) {
    const categoryKey = TRAIT_KEYS_BY_TYPE[category as EvaluationTraitCategory]

    // Sort traits alphabetically by name within each category
    const sortedTraits = orderBy(traits, "name", "asc")

    // Generate fields for each trait in this category
    sortedTraits.forEach((trait, index) => {
      const counter = index + 1 // 1-based counter
      const textKey = `${prefix}${categoryKey}text${counter}` as keyof T
      const checkKey = `${prefix}${categoryKey}check${counter}` as keyof T

      result[textKey] = trait.name as T[keyof T]
      result[checkKey] = (checkboxes[trait.id] || false) as T[keyof T]
    })
  }

  console.log(`🔧 Generated ${prefix} data:`, result)

  return result
}

export const getTemplateId = (
  currentSkill: CoreSkillLevel | DigitalSkillLevel | SculptureSkillLevel,
  isSkillUp: boolean,
  classGroup: CLASS_GROUPS
) => {
  // Remove the ^ suffix to get the base skill level
  const baseSkill = currentSkill.replace("^", "")

  const classType = CLASS_GROUP_TO_SKILL_TYPE[classGroup]

  const templateId = getEvalTemplateId(baseSkill, isSkillUp, classType)

  invariant(
    templateId != null && templateId !== "",
    `TEMPLATE NOT FOUND for ${baseSkill} with ${isSkillUp ? "promotion" : "no promotion"} in ${classType}!`
  )

  return templateId
}

export const formatEvalForPrinting = (
  studentName: string,
  evaluation: EvaluationConstructor,
  traitsById: Record<string, EvaluationTrait>
): PrintEvalData => {
  const {
    didWell,
    needsWork,
    secretMessage,
    currentSkill,
    nextSkill,
    checkboxes,
    classGroup,
  } = evaluation

  const isSkillUp = getIsSkillUp(currentSkill, nextSkill, classGroup)

  // Get the next color level (not the evaluation's nextSkill)
  const nextColorLevel = getNextColorLevel(currentSkill, classGroup)

  // Filter traits by skill levels
  const currentBaseSkill = currentSkill.replace("^", "")
  const currentSkillTraits = pickBy(traitsById, (trait) =>
    trait.skillLevels.includes(currentBaseSkill)
  )
  const nextSkillTraits = pickBy(traitsById, (trait) =>
    trait.skillLevels.includes(nextColorLevel)
  )

  // Current data: map current skill traits to evaluation checkboxes
  const currentCheckboxes: Record<string, boolean> = mapValues(
    currentSkillTraits,
    (trait) => checkboxes[trait.id] || false
  )

  const currentData = getTraitDataForPrint<PrintEvalCurrentCheckboxes>(
    "current",
    currentCheckboxes,
    currentSkillTraits
  )

  // Next data: map next skill traits to empty values (unchecked)
  const nextCheckboxes: Record<string, boolean> = mapValues(
    nextSkillTraits,
    () => false
  )
  const nextData = getTraitDataForPrint<PrintEvalNextCheckboxes>(
    "next",
    nextCheckboxes,
    nextSkillTraits
  )

  // change current level into text and replace ^ with 2
  const currentSkillLabel =
    EVAL_LEVEL_LABELS_BY_LEVEL[
      currentSkill as keyof typeof EVAL_LEVEL_LABELS_BY_LEVEL
    ]
  const nextSkillLabel =
    EVAL_LEVEL_LABELS_BY_LEVEL[
      nextSkill as keyof typeof EVAL_LEVEL_LABELS_BY_LEVEL
    ]

  // find image for eval
  const imageKey = currentSkill.replace(
    "^",
    ""
  ) as keyof typeof EVAL_LEVEL_ICONS
  const iconUrl = EVAL_LEVEL_ICONS[imageKey] || null // send this as a param

  // Currently these are hard coded in evaluationConstants.ts
  // const url = await this.props.storage.refFromURL(iconUrl).getDownloadURL(); // needs auth from correct db

  // only students who don't level up get messages
  // Red students cannot level up and do not get messages
  // Students who level up get the icon

  const dataToSubmit: PrintEvalData = {
    studentname: studentName,
    currentlevel: currentSkillLabel,
    nextlevel: nextSkillLabel,
    icon: { url: iconUrl || "" },
    ...currentData,
    ...nextData,
  }

  if (!isSkillUp) {
    dataToSubmit.thumbsup = didWell || ""
    dataToSubmit.needstoworkon = needsWork || ""
    dataToSubmit.secretmessage = secretMessage || ""
  }

  return dataToSubmit
}

/**
 * Picks the most recent evaluation from a STRICTLY EARLIER semester than
 * `currentSemester`. Semester ids ("YYYY X") sort chronologically as strings.
 *
 * Positional lookups (sort desc, take index + 1) are not safe here: they
 * return a same-semester sibling when duplicate docs exist, and fall back to
 * the NEWEST eval when the current eval is missing from the list.
 */
export const findPreviousEval = (
  evaluations: EvaluationConstructor[],
  currentSemester: string
): EvaluationConstructor | null => {
  const sortedEvals = orderBy(evaluations, "semester", "desc")
  return (
    sortedEvals.find((evaluation) => evaluation.semester < currentSemester) ??
    null
  )
}

export const getPrevEval = async (
  studentId: Uuid,
  classGroup: CLASS_GROUPS,
  currentSemester: string
) => {
  const evalQuery = {
    queries: {
      studentId: {
        value: studentId,
        comparator: COMPARATORS.EQUALS,
      },
      classGroup: {
        value: classGroup,
        comparator: COMPARATORS.EQUALS,
      },
    },
  }

  const allEvals =
    await Firebase.getInstance().variableQuery<EvaluationConstructor>(
      DOC_TYPES.EVALUATIONS,
      evalQuery
    )

  const prevEval = findPreviousEval(allEvals, currentSemester)

  const prevEvalTraitIds = isEmpty(prevEval?.checkboxes)
    ? [NULL_ID]
    : Object.keys(prevEval.checkboxes)

  const evalTraitsQuery = {
    queries: {
      id: {
        value: prevEvalTraitIds,
        comparator: COMPARATORS.IN,
      },
    },
  }

  const prevEvalTraits =
    await Firebase.getInstance().variableQuery<EvaluationTrait>(
      DOC_TYPES.EVAL_TRAITS,
      evalTraitsQuery
    )

  const prevEvalTraitsById = keyBy(prevEvalTraits, "id")

  return {
    prevEval,
    prevEvalTraitsById,
  }
}
